import { tv, type VariantProps } from 'tailwind-variants'
import { twMerge } from 'tailwind-merge'
import type { ComponentProps } from 'react'

export const inputVariants = tv({
	base: [
		'w-full rounded-lg border border-input bg-surface px-3 py-2 text-[16px] md:text-sm text-foreground',
		'placeholder:text-muted-foreground',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
		'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
		'data-[error]:border-destructive data-[error]:focus-visible:ring-destructive',
	],
	variants: {
		size: {
			sm: 'h-6 text-[16px] md:text-xs px-2',
			md: 'h-9 text-[16px] md:text-sm px-3',
			lg: 'h-10 text-base px-4',
		},
	},
	defaultVariants: { size: 'md' },
})

export interface InputProps
	extends Omit<ComponentProps<'input'>, 'size'>,
		VariantProps<typeof inputVariants> {
	error?: boolean
}

export function Input({ className, size, disabled, error, ...props }: InputProps) {
	return (
		<input
			data-slot="input"
			data-disabled={disabled ? '' : undefined}
			data-error={error ? '' : undefined}
			className={twMerge(inputVariants({ size }), className)}
			disabled={disabled}
			{...props}
		/>
	)
}

export interface LabelProps extends ComponentProps<'label'> {}

export function Label({ className, ...props }: LabelProps) {
	return (
		<label
			data-slot="label"
			className={twMerge('text-sm font-medium text-foreground', className)}
			{...props}
		/>
	)
}

export interface FormFieldProps extends ComponentProps<'div'> {}

export function FormField({ className, ...props }: FormFieldProps) {
	return (
		<div
			data-slot="form-field"
			className={twMerge('flex flex-col gap-1.5', className)}
			{...props}
		/>
	)
}

export interface FormErrorProps extends ComponentProps<'p'> {}

export function FormError({ className, ...props }: FormErrorProps) {
	return (
		<p
			data-slot="form-error"
			className={twMerge('text-xs text-destructive', className)}
			{...props}
		/>
	)
}
